import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { fansAxisIndexes, FansGrowthBucket, formatBucketDay } from './fansGrowthSeries';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansGrowthChartProps {
  buckets: FansGrowthBucket[];
  /** The bucket under the pointer, or null. */
  active: number | null;
  onActive: (index: number | null) => void;
}

/**
 * One bar pair per day (per week past 90 days): subscribed up from the baseline, unsubscribed down
 * (design c1). Both halves share one scale: each half is as tall as its own peak, so a bar of 4
 * fans has the same length up and down.
 */
const FansGrowthChartComponent: React.FC<FansGrowthChartProps> = ({ buckets, active, onActive }) => {
  const intl = useIntl();

  const peaks = useMemo(
    () => ({
      up: buckets.reduce((max, bucket) => Math.max(max, bucket.subscribed), 0),
      down: buckets.reduce((max, bucket) => Math.max(max, bucket.unsubscribed), 0),
    }),
    [buckets]
  );

  // The two halves split the height by their peaks, so one fan is the same length in both.
  const rows = useMemo(() => {
    const sum = Math.max(peaks.up + peaks.down, 1);
    return { gridTemplateRows: `${Math.max(peaks.up, 1) / sum}fr ${peaks.down / sum}fr` };
  }, [peaks]);

  const columns = useMemo(
    () =>
      buckets.map((bucket, index) => (
        <div
          key={bucket.start}
          className={styles.growthSlot}
          style={rows}
          data-active={active === index ? 'true' : undefined}
          onMouseEnter={() => onActive(index)}
        >
          <div className={styles.growthUpCell}>
            {bucket.subscribed > 0 ? (
              <i className={styles.growthUp} style={{ height: `${(bucket.subscribed / peaks.up) * 100}%` }} />
            ) : null}
          </div>
          <div className={styles.growthDownCell}>
            {bucket.unsubscribed > 0 ? (
              <i className={styles.growthDown} style={{ height: `${(bucket.unsubscribed / peaks.down) * 100}%` }} />
            ) : null}
          </div>
        </div>
      )),
    [active, buckets, onActive, peaks, rows]
  );

  const axis = useMemo(
    () =>
      fansAxisIndexes(buckets.length).map((index) => {
        const bucket = buckets[index];
        return bucket === undefined ? null : <span key={bucket.start}>{formatBucketDay(intl, bucket.start)}</span>;
      }),
    [buckets, intl]
  );

  return (
    <div className={styles.growth} onMouseLeave={() => onActive(null)}>
      <div className={styles.growthBars} aria-hidden="true">
        {columns}
      </div>
      <div className={styles.growthAxis}>{axis}</div>
    </div>
  );
};

export const FansGrowthChart = TypedMemo(FansGrowthChartComponent);
